import {
  Alert, Box, CircularProgress, Paper, Tooltip, Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AboutUs from 'api/admin/clinic-manage/about.api'
import DashboardConfigsApi from 'api/executive/dashboard-configs.api'
import { useOperativeTypes } from 'features/executive/shared/hooks/use-reference-data'
import ButtonCustom from 'component/Button/ButtonCustom'
import ExcelCodeFilterSelect, { CodeRow } from 'component/Search/ExcelCodeFilterSelect'
import PageModal from 'new-components/modal/page-modal'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import InfoBanner from 'new-components/banners/info-banner'
import TabLoading from './TabLoading'

/**
 * ประเภทหัตถการ (Procedure-type filter) — admin picks one focus set of
 * operative types. The "รายได้เฉลี่ยต่อคนไข้" dashboard widget then offers
 * three view modes against that set:
 *   • ทั้งหมด        — no filter
 *   • เฉพาะที่เลือก   — only patients whose orders use a focus type
 *   • ไม่รวมที่เลือก  — patients whose orders use none of the focus types
 *
 * Persisted under the `procedure_type_filter` dashboard config as a flat
 * { operativeTypeIds: number[] }. Replaces the old hardcoded "exclude Ortho"
 * toggle — filtering is now by operativeTypeId, so it is rename-proof and
 * works regardless of how a clinic names its categories.
 */
export default function ProcedureTypesTab() {
  const { t } = useTranslation()
  const { data: operativeTypes, loading: typesLoading } = useOperativeTypes()
  const [aboutUsId, setAboutUsId] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [baseline, setBaseline] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isConfigured, setIsConfigured] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)

  useEffect(() => {
    AboutUs.findAboutUs()
      .then((res) => {
        const id = Number((res?.data as { aboutUsId?: number })?.aboutUsId)
        if (Number.isFinite(id) && id > 0) setAboutUsId(id)
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    if (aboutUsId == null) return
    setLoading(true)
    DashboardConfigsApi.getProcedureTypeFilter(aboutUsId)
      .then((r) => {
        setSelectedIds(r.operativeTypeIds)
        setBaseline(r.operativeTypeIds)
        setIsConfigured(r.isConfigured)
      })
      .catch(() => {
        setSelectedIds([])
        setBaseline([])
      })
      .finally(() => setLoading(false))
  }, [aboutUsId])

  // Adapt operativeTypes ({id,name}) → CodeRow ({id,value=name}) so the Excel
  // picker — which filters and displays by `value` — can show full names.
  const rows: CodeRow[] = useMemo(
    () => operativeTypes.map((o) => ({ id: o.id, value: o.name })),
    [operativeTypes],
  )
  const selectedRows: CodeRow[] = useMemo(() => {
    const set = new Set(selectedIds)
    return rows.filter((r) => set.has(r.id as number))
  }, [rows, selectedIds])

  const hasUnsavedChanges = useMemo(() => {
    if (selectedIds.length !== baseline.length) return true
    const a = [...selectedIds].sort((x, y) => x - y)
    const b = [...baseline].sort((x, y) => x - y)
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true
    return false
  }, [selectedIds, baseline])

  const saveDisabledReason: string | null = saving
    ? (t('DASHBOARD_TARGETS.SAVE_IN_FLIGHT', { defaultValue: 'กำลังบันทึก...' }) as string)
    : aboutUsId == null
      ? (t('DASHBOARD_TARGETS.NO_ABOUT_US_SHORT', { defaultValue: 'ยังโหลดข้อมูลคลินิกไม่สำเร็จ' }) as string)
      : !hasUnsavedChanges
        ? (t('DASHBOARD_TARGETS.NOTHING_TO_SAVE', { defaultValue: 'ยังไม่มีการเปลี่ยนแปลง' }) as string)
        : null

  /** Roll the in-progress selection back to whatever was last loaded from
   *  the server. Triggered from the toolbar's "รีเซ็ต" button. */
  const doReset = () => {
    setSelectedIds(baseline)
    setValidationError(null)
    notiSuccess(t('DASHBOARD_TARGETS.RESET_SUCCESS', { defaultValue: 'รีเซ็ตการแก้ไขแล้ว' }) as string)
  }

  const save = async () => {
    if (aboutUsId == null) return
    if (selectedIds.length === 0) {
      setValidationError(
        t('DASHBOARD_TARGETS.PROC_TYPES.NEED_ONE', 'เลือกประเภทหัตถการอย่างน้อย 1 รายการ') as string
      )
      return
    }
    setValidationError(null)
    setSaving(true)
    try {
      await DashboardConfigsApi.saveProcedureTypeFilter(aboutUsId, selectedIds)
      setBaseline(selectedIds)
      setIsConfigured(true)
      notiSuccess(t('DASHBOARD_TARGETS.SAVE_SUCCESS') as string)
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      notiError(typeof msg === 'string' ? msg : (t('DASHBOARD_TARGETS.SAVE_ERROR') as string))
    } finally {
      setSaving(false)
    }
  }

  if (loading || typesLoading) {
    return <TabLoading />
  }

  return (
    <Box>
      <InfoBanner sx={{ mb: 2 }}>
        {t(
          'DASHBOARD_TARGETS.PROC_TYPES.DESCRIPTION',
          'เลือกประเภทหัตถการที่ต้องการโฟกัส — แดชบอร์ด "รายได้เฉลี่ยต่อคนไข้" จะเลือกดูได้ 3 แบบ: ทั้งหมด / เฉพาะประเภทที่เลือก / ตัดประเภทที่เลือกออก'
        )}
        {' '}
        {t(
          'DASHBOARD_TARGETS.PROC_TYPES.RULE_HINT',
          'กติกาการนับ: ออร์เดอร์ที่มีหัตถการในประเภทที่เลือกอย่างน้อย 1 รายการ ถือว่า "เข้าข่าย"'
        )}
        {!isConfigured && (
          <> — {t(
            'DASHBOARD_TARGETS.PROC_TYPES.NO_CONFIG',
            'ยังไม่ได้ตั้งค่า — แดชบอร์ดจะมีแค่โหมด "ทั้งหมด" จนกว่าจะเลือกประเภทแล้วกดบันทึก'
          )}</>
        )}
      </InfoBanner>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'flex-end' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <ButtonCustom
            variant="outlined"
            onClick={() => setConfirmReset(true)}
            disabled={saving || !hasUnsavedChanges}
            textButton={t('DASHBOARD_TARGETS.RESET_BUTTON', { defaultValue: 'รีเซ็ต' })}
          />
          <Tooltip title={saveDisabledReason ?? ''} placement="top">
            <span>
              <ButtonCustom
                variant="contained"
                onClick={save}
                disabled={Boolean(saveDisabledReason)}
                textButton={saving ? <CircularProgress size={20} /> : t('DASHBOARD_TARGETS.SAVE')}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {t('DASHBOARD_TARGETS.PROC_TYPES.LABEL', 'ประเภทหัตถการที่ใช้กรอง')}
        </Typography>
        <Box sx={{ maxWidth: 520 }}>
          <ExcelCodeFilterSelect
            data={rows}
            selectedRows={selectedRows}
            onSelectionChange={(picked) => setSelectedIds(picked.map((r) => r.id as number))}
            inputLabel={t('DASHBOARD_TARGETS.PROC_TYPES.LABEL', 'ประเภทหัตถการ') as string}
            placeholder={t('DASHBOARD_TARGETS.PROC_TYPES.SEARCH', 'ค้นหาประเภทหัตถการ...') as string}
            maxTags={5}
            rowHeight={32}
            maxListHeight={220}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('DASHBOARD_TARGETS.PROC_TYPES.SELECTED', 'เลือกแล้ว')} {selectedIds.length} /{' '}
          {operativeTypes.length}
        </Typography>
      </Paper>

      <PageModal
        isOpen={confirmReset}
        size="XS"
        title={t('DASHBOARD_TARGETS.RESET_TITLE', { defaultValue: 'ยืนยันรีเซ็ตการแก้ไข' }) as string}
        handleClose={() => setConfirmReset(false)}
        onCancel={{
          onClick: () => setConfirmReset(false),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            setConfirmReset(false)
            doReset()
          },
          title: t('DASHBOARD_TARGETS.RESET_CONFIRM', { defaultValue: 'รีเซ็ต' }) as string,
          type: 'danger',
        }}
      >
        <Typography variant="body2">
          {t('DASHBOARD_TARGETS.RESET_BODY', {
            defaultValue: 'หมายเหตุ: การเปลี่ยนแปลงที่ยังไม่บันทึกทั้งหมดจะถูกรีเซ็ต',
          })}
        </Typography>
      </PageModal>
    </Box>
  )
}
